/**
 * Array manipulation utilities
 */

export function first<T>(array: T[]): T | undefined {
  return array[0];
}

export function sum(numbers: number[]): number {
  return numbers.reduce((total, num) => total + num, 0);
}

export function sample<T>(array: T[]): T | undefined {
  const index = Math.floor(Math.random() * array.length);
  return array[index];
}

/**
 * This function sorts an array into random order
 */
export function shuffle<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = 0; i < result.length; i++) {
    const randomIndex = Math.floor(Math.random() * result.length);
    const temp = result[i];
    result[i] = result[randomIndex];
    result[randomIndex] = temp;
  }
  return result;
}

/**
 * Swap two elements in an array
 */
export function arraySwap<T>(array: T[], index1: number, index2: number): void {
  if (index1 !== index2 && index1 >= 0 && index2 >= 0 && index1 < array.length && index2 < array.length) {
    const temp = array[index1];
    array[index1] = array[index2];
    array[index2] = temp;
  }
}

export function removeMultipleIndices<T>(array: T[], indices: number[]): void {
  indices
    .toSorted((a, b) => b - a)
    .forEach((index) => {
      array.splice(index, 1);
    });
}

/**
 * This function filters an array to unique items based on the return of the
 * given function for each item
 */
export function uniqBy<T>(array: T[], fn: (item: T) => any): T[] {
  const seen = new Set();
  return array.filter((item) => {
    const key = fn(item);
    return !seen.has(key) && seen.add(key);
  });
}

export function last<T>(array: T[]): T | undefined {
  return array[array.length - 1];
}

/**
 * Creates an array of grouped elements, where the first element contains the first elements of the given arrays,
 * the second contains the second elements, and so on. If arrays have different lengths, the resulting array
 * will be as long as the shortest input array.
 *
 * @example
 * zip([1, 2], ['a', 'b']); // [[1, 'a'], [2, 'b']]
 * zip([1, 2], ['a', 'b'], [true, false]); // [[1, 'a', true], [2, 'b', false]]
 * zip([1, 2, 3], ['a', 'b']); // [[1, 'a'], [2, 'b']]
 */
export function zip<T extends any[][]>(
  ...arrays: T
): Array<{ [K in keyof T]: T[K] extends Array<infer U> ? U : never }> {
  if (arrays.length === 0) return [];

  // Find the length of the shortest array
  const minLength = Math.min(...arrays.map((arr) => arr.length));

  // Create the zipped array
  const result = Array(minLength);

  for (let i = 0; i < minLength; i++) {
    result[i] = arrays.map((arr) => arr[i]) as any;
  }

  return result;
}

export function isEmpty<T>(array: T[]): boolean {
  return array.length === 0;
}

export function isNotEmpty<T>(array: T[]): boolean {
  return array.length > 0;
}
